import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import {
  fetchNotifications,
  markNotificationAsRead,
  markNotificationsAsRead,
  savePushSubscription,
} from "../service/notificationService";
import {
  getNewUnreadNotifications,
  getUnreadNotificationIds,
} from "../utils/notifications";
import {
  buildNotificationEventKey,
  shouldShowBrowserNotification,
  shouldShowToastNotification,
} from "../utils/notificationDisplay";
import {
  getBrowserNotificationPermission,
  requestBrowserNotificationPermission,
  sendBrowserNotification,
} from "../utils/browserNotifications";
import {
  isPushSupported,
  registerPushServiceWorker,
  subscribeToPushNotifications,
} from "../utils/pushNotifications";
import {
  bindVehicleReadyRequestListener,
  createVehicleReadySocket,
} from "../service/socketService";

const NotificationContext = createContext(null);
const POLL_INTERVAL_MS = 15000;
const NOTIFICATION_PAGE_SIZE = 10;
const NOTIFICATION_DEDUPE_WINDOW_MS = 30000;

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketError, setSocketError] = useState("");
  const [notificationPermission, setNotificationPermission] = useState(() =>
    getBrowserNotificationPermission()
  );
  const [pushStatus, setPushStatus] = useState("idle");
  const hasLoadedInitiallyRef = useRef(false);
  const seenNotificationIdsRef = useRef(new Set());
  const announcedNotificationKeysRef = useRef(new Map());
  const pushSetupPromiseRef = useRef(null);

  const syncSeenNotifications = useCallback((items) => {
    seenNotificationIdsRef.current = new Set(items.map((item) => item.id));
  }, []);

  const announceNotification = useCallback((notification) => {
    const eventKey = buildNotificationEventKey(notification);
    const now = Date.now();
    const lastAnnouncedAt = announcedNotificationKeysRef.current.get(eventKey);

    if (
      typeof lastAnnouncedAt === "number" &&
      now - lastAnnouncedAt < NOTIFICATION_DEDUPE_WINDOW_MS
    ) {
      return;
    }

    announcedNotificationKeysRef.current.set(eventKey, now);

    const isDocumentHidden =
      typeof document !== "undefined" ? document.hidden : false;

    if (shouldShowToastNotification(isDocumentHidden)) {
      toast.info(
        <div>
          <strong>{notification.title}</strong>
          <p className="mb-0">{notification.body || notification.message}</p>
        </div>,
        {
          toastId: `notification-${eventKey}`,
        }
      );
    }

    if (shouldShowBrowserNotification(isDocumentHidden)) {
      sendBrowserNotification({
        title: notification.title,
        body: notification.body || notification.message,
        onClick: () => {
          window.focus();
          window.location.hash = "#/notifications";
        },
      });
    }
  }, []);

  const refreshNotifications = useCallback(async () => {
    const token = localStorage.getItem("prime_access_token");

    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      seenNotificationIdsRef.current = new Set();
      hasLoadedInitiallyRef.current = false;
      return;
    }

    setLoading(true);

    try {
      const { notifications: nextNotifications, unreadCount: nextUnreadCount } =
        await fetchNotifications({
          page: 1,
          limit: NOTIFICATION_PAGE_SIZE,
        });

      const newlyArrived =
        hasLoadedInitiallyRef.current
          ? getNewUnreadNotifications(
              nextNotifications,
              seenNotificationIdsRef.current
            )
          : [];

      setNotifications(nextNotifications);
      setUnreadCount(nextUnreadCount);
      syncSeenNotifications(nextNotifications);
      setRefreshVersion((currentValue) => currentValue + 1);

      newlyArrived.forEach((notification) => {
        announceNotification(notification);
      });

      hasLoadedInitiallyRef.current = true;
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [announceNotification, syncSeenNotifications]);

  useEffect(() => {
    refreshNotifications();

    const intervalId = window.setInterval(
      refreshNotifications,
      POLL_INTERVAL_MS
    );

    return () => window.clearInterval(intervalId);
  }, [refreshNotifications]);

  const setupPushNotifications = useCallback(async () => {
    const token = localStorage.getItem("prime_access_token");
    const vapidPublicKey = process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY;

    if (!isPushSupported()) {
      setPushStatus("unsupported");
      return null;
    }

    if (!vapidPublicKey) {
      setPushStatus("not-configured");
      return null;
    }

    if (!token) {
      setPushStatus("idle");
      return null;
    }

    if (pushSetupPromiseRef.current) {
      return pushSetupPromiseRef.current;
    }

    pushSetupPromiseRef.current = (async () => {
      try {
        setPushStatus("registering");
        const registration = await registerPushServiceWorker();
        const subscription = await subscribeToPushNotifications({
          serviceWorkerRegistration: registration,
          vapidPublicKey,
        });

        if (!subscription) {
          setPushStatus("unsupported");
          return null;
        }

        await savePushSubscription({
          subscription,
          token,
        });

        setPushStatus("subscribed");
        return subscription;
      } catch (error) {
        console.error("Push notification setup failed:", error);
        setPushStatus("error");
        return null;
      } finally {
        pushSetupPromiseRef.current = null;
      }
    })();

    return pushSetupPromiseRef.current;
  }, []);

  const enableBrowserNotifications = useCallback(async () => {
    try {
      const permission = await requestBrowserNotificationPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        await setupPushNotifications();
      }

      return permission;
    } catch (error) {
      console.error("Notification permission request failed:", error);
      setSocketError("Unable to enable desktop notifications.");
      return "error";
    }
  }, [setupPushNotifications]);

  useEffect(() => {
    if (notificationPermission === "granted") {
      setupPushNotifications();
    }
  }, [notificationPermission, setupPushNotifications]);

  useEffect(() => {
    const token = localStorage.getItem("prime_access_token");

    if (!token) {
      return undefined;
    }

    const socket = createVehicleReadySocket({
      ioClient: io,
      baseURL: process.env.REACT_APP_BASE_URL,
    });

    socket.on("connect", () => {
      setSocketConnected(true);
      setSocketError("");
    });

    socket.on("disconnect", () => {
      setSocketConnected(false);
    });

    socket.on("connect_error", (error) => {
      setSocketConnected(false);
      setSocketError(error?.message || "Realtime socket connection failed.");
    });

    const unsubscribe = bindVehicleReadyRequestListener(socket, async (data) => {
      announceNotification({
        title: data.title || "New Vehicle Ready Request",
        body: data.message,
        bookingRequestId: data.bookingRequestId,
      });

      await refreshNotifications();
    });

    return () => {
      unsubscribe();
      socket.disconnect();
    };
  }, [announceNotification, refreshNotifications]);

  const handleMarkAsRead = useCallback(
    async (notificationId) => {
      await markNotificationAsRead(notificationId);
      await refreshNotifications();
      toast.success("Notification marked as read.");
    },
    [refreshNotifications]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    const unreadIds = getUnreadNotificationIds(notifications);

    if (unreadIds.length === 0) {
      return;
    }

    await markNotificationsAsRead(unreadIds);
    await refreshNotifications();
  }, [notifications, refreshNotifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      refreshVersion,
      socketConnected,
      socketError,
      notificationPermission,
      pushStatus,
      refreshNotifications,
      markAsRead: handleMarkAsRead,
      markAllAsRead: handleMarkAllAsRead,
      enableBrowserNotifications,
    }),
    [
      notifications,
      unreadCount,
      loading,
      refreshVersion,
      socketConnected,
      socketError,
      notificationPermission,
      pushStatus,
      refreshNotifications,
      handleMarkAsRead,
      handleMarkAllAsRead,
      enableBrowserNotifications,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }

  return context;
};
